import {
  SettingsType,
  type SettingInfoDto,
} from '../../../shared/api/pipeex/pcfSettingsClient';
import { isGrpcRuntimeAvailable } from '../../../shared/api/grpc/transport';
import type { PipingSystem } from '../../export-pcf';

export function uniqueNonEmptyStrings(values: Array<string | number | null | undefined>): string[] {
  return Array.from(
    new Set(
      values
        .map((value) => String(value ?? '').trim())
        .filter((value) => value.length > 0)
    )
  );
}

export function uniqueSystemScopeNames(values: Array<string | number | null | undefined>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) {
      continue;
    }

    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    result.push(trimmed);
  }

  return result;
}

export function parseMappingSettingsType(raw: unknown): SettingsType {
  if (typeof raw === 'number') {
    return raw === SettingsType.Company ? SettingsType.Company : SettingsType.User;
  }

  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase();
    if (normalized === '0' || normalized === 'company') {
      return SettingsType.Company;
    }
    if (normalized === '1' || normalized === 'user') {
      return SettingsType.User;
    }
  }

  return SettingsType.User;
}

export function getProfileKeyParts(profileKey: string): { name: string; settingsType: SettingsType } {
  const [name, settingsTypeRaw] = profileKey.split('::');
  return {
    name: name || 'Default',
    settingsType: parseMappingSettingsType(settingsTypeRaw),
  };
}

export function buildSettingInfoProfileKey(profile: Pick<SettingInfoDto, 'name' | 'settingsType'>): string {
  return `${profile.name}::${parseMappingSettingsType(profile.settingsType)}`;
}

export function isRevitProjectIdentityPending(projectKey: string | null | undefined): boolean {
  return isGrpcRuntimeAvailable() && !projectKey;
}

export function isE2eMappingBypassActive(): boolean {
  if (
    process.env.REACT_APP_PIPEEX_E2E_BYPASS !== '1' ||
    typeof window === 'undefined'
  ) {
    return false;
  }

  return new URLSearchParams(window.location.search).get('e2e') === '1';
}

export function collectFocusedElementIds(system: PipingSystem): string[] {
  const validationScopeElementIds = Array.isArray(system.validationScopeElementIds)
    ? system.validationScopeElementIds
    : [];
  const systemElementIds = Array.isArray(system.elementIds) ? system.elementIds : [];
  const preferredElementIds =
    validationScopeElementIds.length > 0 ? validationScopeElementIds : systemElementIds;

  return uniqueNonEmptyStrings(preferredElementIds);
}

export function collectFocusedSystemNames(system: PipingSystem): string[] {
  return uniqueSystemScopeNames([
    system.systemName,
    system.sourceSystemName,
    ...(typeof system.sourceSystemNames === 'string' ? system.sourceSystemNames.split(/[;,|]+/) : []),
    system.systemTypeName,
    ...(Array.isArray(system.validationScopeSystemNames) ? system.validationScopeSystemNames : []),
  ]);
}
